import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { QrCodeIcon, Search, ChevronDown, ChevronRight, HelpCircle, MessageCircle, Book } from 'lucide-react';

export default function Help() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);

  const helpSections = [
    {
      title: "Getting Started",
      icon: "🚀",
      articles: [
        { title: "How to create your first QR code", content: "Sign up for an account, navigate to your dashboard, click 'Generate QR Code', add a title and greeting message, then download your QR code image." },
        { title: "Understanding QR code ownership", content: "The first person to scan your QR code becomes the 'Owner'. All subsequent messages sent through that QR code will be delivered to the owner's email." },
        { title: "Setting up email notifications", content: "Email notifications are automatically enabled when you create an account. You'll receive an email every time someone sends you a message through your QR codes." }
      ]
    },
    {
      title: "Managing Messages",
      icon: "💬",
      articles: [
        { title: "How to reply to messages", content: "Go to your dashboard, find the message you want to reply to, and click 'Reply'. Your response will be sent anonymously to the message sender." },
        { title: "Blocking unwanted messages", content: "You can block specific senders or disable a QR code entirely from your dashboard. Blocked senders won't be able to contact you through any of your QR codes." },
        { title: "Message history and threads", content: "All messages are organized by QR code in your dashboard. You can view conversation history and see which QR code each message came from." }
      ]
    },
    {
      title: "QR Code Management",
      icon: "📱",
      articles: [
        { title: "Editing QR code information", content: "You can update the title, greeting message, and settings for your QR codes at any time from your dashboard." },
        { title: "Downloading and printing QR codes", content: "QR codes can be downloaded as high-resolution PNG images suitable for printing on stickers, business cards, or any other medium." },
        { title: "QR code limits by plan", content: "Free users can create up to 10 QR codes. Basic plan allows 100 QR codes, and Premium plan offers unlimited QR code generation." }
      ]
    },
    {
      title: "Privacy & Security",
      icon: "🔒",
      articles: [
        { title: "How your privacy is protected", content: "Your personal contact information is never shared with message senders. All communication is routed through our secure platform to maintain anonymity." },
        { title: "Data retention policies", content: "Messages are stored securely and can be deleted at any time. We never sell or share your personal information with third parties." },
        { title: "Anonymous messaging explained", content: "Senders can include their contact info optionally, but you choose whether to respond. Your identity remains protected unless you choose to share it." }
      ]
    },
    {
      title: "Account & Billing",
      icon: "💳",
      articles: [
        { title: "Upgrading your account", content: "You can upgrade to Basic or Premium plans at any time from your dashboard. Upgrades take effect immediately." },
        { title: "Usage limits and overages", content: "Each plan has specific limits for QR codes and monthly messages. We'll notify you when approaching limits and offer upgrade options." },
        { title: "Canceling your subscription", content: "You can cancel your subscription at any time. Your account will remain active until the end of your current billing period." }
      ]
    },
    {
      title: "Troubleshooting",
      icon: "🔧",
      articles: [
        { title: "QR code not working", content: "Ensure the QR code image is clear and not damaged. Try scanning with different QR code reader apps. Contact support if issues persist." },
        { title: "Not receiving email notifications", content: "Check your spam folder first. Ensure your email address is correct in account settings. Add support@icomment.app to your contacts." },
        { title: "Can't access my account", content: "Use the 'Forgot Password' link on the login page. If you used email/SMS login, request a new login link. Contact support for further assistance." }
      ]
    }
  ];

  const filteredSections = helpSections.map(section => ({
    ...section,
    articles: section.articles.filter(
      article => 
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.content.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.articles.length > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900">Home</Link>
              <Link to="/auth" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Sign In</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Help Center
            <span className="block text-yellow-300">Find Answers Fast</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Everything you need to know about using iComment.app effectively.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-6 py-4 text-lg text-gray-900 rounded-lg border-0 focus:ring-2 focus:ring-white focus:ring-opacity-50"
            />
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <Book className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">User Guide</h3>
              <p className="text-gray-600 mb-4">Complete guide to using all features</p>
              <button className="text-blue-600 hover:text-blue-800 font-medium">Read Guide</button>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <MessageCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Contact Support</h3>
              <p className="text-gray-600 mb-4">Get personalized help from our team</p>
              <Link to="/contact" className="text-green-600 hover:text-green-800 font-medium">Get Help</Link>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <HelpCircle className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">FAQ</h3>
              <p className="text-gray-600 mb-4">Quick answers to common questions</p>
              <button className="text-purple-600 hover:text-purple-800 font-medium">View FAQ</button>
            </div>
          </div>
        </div>
      </section>

      {/* Help Articles */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="space-y-6">
            {filteredSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="bg-white rounded-lg shadow-md">
                <button
                  onClick={() => setExpandedSection(expandedSection === sectionIndex ? null : sectionIndex)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{section.icon}</span>
                    <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
                  </div>
                  {expandedSection === sectionIndex ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                {expandedSection === sectionIndex && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <div className="space-y-4 pt-4">
                      {section.articles.map((article, articleIndex) => (
                        <div key={articleIndex} className="border-l-4 border-blue-200 pl-4">
                          <h4 className="font-medium text-gray-900 mb-2">{article.title}</h4>
                          <p className="text-gray-600 leading-relaxed">{article.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {filteredSections.length === 0 && searchTerm && (
            <div className="text-center py-12">
              <HelpCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search terms or browse all categories above.</p>
              <Link 
                to="/contact"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Contact Support
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}